import { AutoAwesome, Close, Refresh, Settings } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

interface NoPlanDialogProps {
  open: boolean;
  onClose: () => void;
  onRunOnboarding: () => void;
  onGeneratePlan: () => void;
  isGenerating?: boolean;
}

/**
 * Dialog shown when user has no valid training plan in the database.
 * Gives options to run onboarding wizard, generate a plan, or cancel.
 */
const NoPlanDialog: React.FC<NoPlanDialogProps> = ({
  open,
  onClose,
  onRunOnboarding,
  onGeneratePlan,
  isGenerating = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="no-plan-dialog-title"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <DialogTitle id="no-plan-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            No Training Plan Found
          </Typography>
          <IconButton aria-label="Close dialog" onClick={onClose} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          You don't have an active training plan. Would you like to set up your training preferences
          first, or generate a plan with your current settings?
        </Typography>

        <Stack spacing={2}>
          {/* Onboarding Wizard Option */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={<Settings />}
            onClick={onRunOnboarding}
            disabled={isGenerating}
            aria-label="Run onboarding wizard to set up training preferences"
            sx={{
              py: 1.5,
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
          >
            <Box sx={{ ml: 1 }}>
              <Typography variant="button" component="div">
                Set Up Training Preferences
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Configure your pace, weekly volume, and schedule
              </Typography>
            </Box>
          </Button>

          {/* Generate Plan Option */}
          <Button
            variant="outlined"
            color="primary"
            size="large"
            fullWidth
            startIcon={
              isGenerating ? (
                <AutoAwesome sx={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Refresh />
              )
            }
            onClick={onGeneratePlan}
            disabled={isGenerating}
            aria-label="Generate training plan with current settings"
            sx={{
              py: 1.5,
              justifyContent: 'flex-start',
              textAlign: 'left',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <Box sx={{ ml: 1 }}>
              <Typography variant="button" component="div">
                {isGenerating ? 'Generating Plan...' : 'Generate Plan Now'}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {isGenerating
                  ? 'Please wait while we create your plan'
                  : 'Use default or previously saved settings'}
              </Typography>
            </Box>
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={isGenerating}
          aria-label="Cancel and close dialog"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoPlanDialog;
